import React, { useState } from 'react';
import ImageUploader from '../images/ImageUploader';

const EventFormModal = ({
  showEventModal,
  closeEventModal,
  eventData,
  setEventData,
  handleInputChange,
  handleSubmit,
  isEditing,
  isSubmitting,
  user,
  errors: propErrors
}) => {
  const [localErrors, setLocalErrors] = useState({});
  const errors = { ...propErrors, ...localErrors };

  if (!showEventModal) return null;

  // Componente interno para selección de hora mejorado
  const TimePicker = ({ 
    name, 
    value, 
    onChange, 
    label, 
    error,
    minTime,
    isEndTime = false
  }) => {
    const [hours, minutes] = value ? value.split(':') : ['', ''];
    
    const hourOptions = Array.from({ length: 24 }, (_, i) => 
      i.toString().padStart(2, '0'));
    
    const minuteOptions = Array.from({ length: 12 }, (_, i) => 
      (i * 5).toString().padStart(2, '0'));

    const handleTimeChange = (type, val) => {
      const newTime = type === 'hour' 
        ? `${val}:${minutes || '00'}` 
        : `${hours || '00'}:${val}`;
      onChange({ target: { name, value: newTime } });
      
      // Validación inmediata al cambiar
      if (name === 'hour' && eventData.hour_end) {
        validateTimes(newTime, eventData.hour_end);
      } else if (name === 'hour_end' && eventData.hour) {
        validateTimes(eventData.hour, newTime);
      }
    };

    const filteredHourOptions = minTime 
      ? hourOptions.filter(h => h >= minTime.split(':')[0]) 
      : hourOptions;

    return (
      <div className={`${isEndTime ? 'mt-4' : ''}`}>
        <label className="block text-gray-700 mb-2 text-sm font-medium flex items-center">
          {isEndTime ? (
            <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {label}
        </label>
        <div className="flex items-center space-x-2">
          <select
            value={hours}
            onChange={(e) => handleTimeChange('hour', e.target.value)}
            className={`flex-1 p-2.5 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
              error ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
            }`}
          >
            <option value="">HH</option>
            {(name === 'hour_end' ? filteredHourOptions : hourOptions).map(hour => (
              <option 
                key={hour} 
                value={hour}
                disabled={
                  name === 'hour_end' && 
                  minTime && 
                  hour === minTime.split(':')[0] && 
                  minutes <= minTime.split(':')[1]
                }
              >
                {hour}
              </option>
            ))}
          </select>
          
          <span className="text-gray-500 font-medium">:</span>
          
          <select
            value={minutes}
            onChange={(e) => handleTimeChange('minute', e.target.value)}
            className={`flex-1 p-2.5 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
              error ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
            }`}
            disabled={!hours}
          >
            <option value="">MM</option>
            {minuteOptions.map(minute => (
              <option 
                key={minute} 
                value={minute}
                disabled={
                  name === 'hour_end' && 
                  minTime && 
                  hours === minTime.split(':')[0] && 
                  minute <= minTime.split(':')[1]
                }
              >
                {minute}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  };

  // Función para validar que la hora de término sea mayor que la de inicio
  const validateTimes = (startTime, endTime) => {
    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      
      const startTotal = startHours * 60 + startMinutes;
      const endTotal = endHours * 60 + endMinutes;
      
      if (endTotal <= startTotal) {
        setLocalErrors(prev => ({
          ...prev,
          hour_end: 'La hora de término debe ser mayor que la hora de inicio'
        }));
        return false;
      } else {
        setLocalErrors(prev => ({ ...prev, hour_end: undefined }));
        return true;
      }
    }
    return true;
  };

  // Manejador de cambios para campos de hora con validación
  const handleTimeChange = (e) => {
    handleInputChange(e);
    
    // Validar solo si ambos campos tienen valor
    if (e.target.name === 'hour' && eventData.hour_end) {
      validateTimes(e.target.value, eventData.hour_end);
    } else if (e.target.name === 'hour_end' && eventData.hour) {
      validateTimes(eventData.hour, e.target.value);
    }
  };

  // Manejador para imagen recortada
  const handleCroppedImage = (croppedImage) => {
    const reader = new FileReader();
    reader.onload = () => {
      setEventData(prev => ({ ...prev, image: reader.result }));
      setLocalErrors(prev => ({ ...prev, image: undefined }));
    };
    reader.readAsDataURL(croppedImage);
  };

  // Manejador para eliminar imagen
  const handleRemoveImage = () => {
    setEventData(prev => ({ ...prev, image: '' }));
    setLocalErrors(prev => ({ ...prev, image: undefined }));
  };

  // Formatear fecha para input type="date"
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Manejador de cambios con limpieza de errores
  const handleChangeWithValidation = (e) => {
    handleInputChange(e);
    setLocalErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Encabezado con gradiente */}
        <div 
          className="p-6 rounded-t-xl"
          style={{
            background: 'linear-gradient(135deg, #09274A 0%, #0488AF 100%)'
          }}
        >
          <div className="flex justify-between items-center">
            <h3 className="text-3xl font-bold text-white">
              {isEditing ? 'Editar Evento' : 'Crear Nuevo Evento'}
            </h3>
            <button 
              onClick={closeEventModal}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Mostrar errores generales */}
          {errors.general && (
            <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
              <p>{errors.general}</p>
            </div>
          )}

          <form onSubmit={(e) => {
            e.preventDefault();
            if (validateTimes(eventData.hour, eventData.hour_end)) {
              handleSubmit(e);
            }
          }} className="space-y-6">
            {/* Card 1: Información básica del organizador */}
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <h4 className="text-xl font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
                Información del Organizador
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Creado por:</label>
                  <input
                    type="text"
                    name="business_name"
                    value={eventData.business_name}
                    className="w-full p-3 border rounded-lg bg-gray-100 cursor-not-allowed text-gray-600"
                    readOnly
                    disabled
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Organizado por:</label>
                  <input
                    type="text"
                    name="organized_by"
                    value={eventData.organized_by}
                    onChange={handleChangeWithValidation}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
                      errors.organized_by ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    required
                  />
                  {errors.organized_by && (
                    <p className="mt-1 text-sm text-red-600">{errors.organized_by}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Colaborador/es (opcional):</label>
                  <input
                    type="text"
                    name="collaborator"
                    value={eventData.collaborator}
                    onChange={handleChangeWithValidation}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Detalles del evento */}
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <h4 className="text-xl font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
                Detalles del Evento
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Título del evento:</label>
                  <input
                    type="text"
                    name="title"
                    value={eventData.title}
                    onChange={handleChangeWithValidation}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
                      errors.title ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    required
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Descripción:</label>
                  <textarea
                    name="description"
                    value={eventData.description}
                    onChange={handleChangeWithValidation}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
                      errors.description ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                    rows="4"
                    required
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: Fecha y hora - Versión mejorada */}
           <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
  <h4 className="text-xl font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
    Fecha y Hora
  </h4>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Fecha del evento */}
    <div>
      <label className="block text-gray-700 mb-2 text-sm font-medium">Fecha del evento:</label>
      <input
        type="date"
        name="date"
        value={formatDateForInput(eventData.date)}
        onChange={handleChangeWithValidation}
        className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
          errors.date ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
        }`}
        required
      />
      {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
    </div>

    {/* Horarios */}
    <div>
      <div className="space-y-4">
        <div>
          <TimePicker
            name="hour"
            value={eventData.hour}
            onChange={handleTimeChange}
            label="Hora de inicio"
            error={errors.hour}
          />
        </div>
        <div>
          <TimePicker
            name="hour_end"
            value={eventData.hour_end}
            onChange={handleTimeChange}
            label="Hora de término"
            error={errors.hour_end}
            minTime={eventData.hour}
            isEndTime={true}
          />
        </div>
      </div>
    </div>
  </div>
</div>


            {/* Card 4: Ubicación y modalidad */}
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <h4 className="text-xl font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
                Ubicación y Modalidad
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Lugar:</label>
                  <input
                    type="text"
                    name="place"
                    value={eventData.place}
                    onChange={handleChangeWithValidation}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all ${
                      errors.place ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                  />
                  {errors.place && (
                    <p className="mt-1 text-sm text-red-600">{errors.place}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Modalidad:</label>
                  <select
                    name="mode"
                    value={eventData.mode}
                    onChange={handleChangeWithValidation}
                    required
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:border-blue-500 transition-all appearance-none bg-white ${
                      errors.mode ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                    }`}
                  >
                    <option value="">Seleccione una opción</option>
                    <option value="in-person">Presencial</option>
                    <option value="hybrid">Híbrido</option>
                    <option value="online">Virtual</option>
                  </select>
                  {errors.mode && (
                    <p className="mt-1 text-sm text-red-600">{errors.mode}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Link de registro (opcional):</label>
                  <input
                    type="text"
                    name="registration_link"
                    value={eventData.registration_link}
                    onChange={handleChangeWithValidation}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-1 text-sm font-medium">Link de reunión (opcional):</label>
                  <input
                    type="text"
                    name="meet_link"
                    value={eventData.meet_link}
                    onChange={handleChangeWithValidation}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Card 5: Imagen del evento */}
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <h4 className="text-xl font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-200">
                Imagen del Evento
              </h4>
              <ImageUploader
                onCropped={handleCroppedImage}
                currentImage={eventData.image}
                onRemoveImage={handleRemoveImage}
              />
              {errors.image && (
                <p className="mt-2 text-sm text-red-600">{errors.image}</p>
              )}
            </div>
            
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={closeEventModal}
                disabled={isSubmitting}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all disabled:opacity-50 shadow-md hover:shadow-lg flex items-center"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 shadow-md hover:shadow-lg flex items-center justify-center min-w-32"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditing ? 'Actualizando...' : 'Creando...'}
                  </>
                ) : (
                  <>
                    {isEditing ? (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Actualizar Evento
                      </>
                    ) : (
                      'Crear Evento'
                    )}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventFormModal;